import { useCallback, useRef } from 'react';

type Tab = {
  id: string;
  index: number;
};

type UseTabsOptions = {
  tabs: Tab[];
  activeTabId: string;
  onTabChange: (tabIndex: number) => void;
  onFocusPanel?: (tabId: string) => void;
};

export const useTabs = ({ tabs, activeTabId, onTabChange, onFocusPanel }: UseTabsOptions) => {
  const tabRefs = useRef<{ [key: string]: HTMLButtonElement | null }>({});

  const getActiveTabIndex = useCallback(() => {
    return tabs.findIndex((tab) => tab.id === activeTabId);
  }, [tabs, activeTabId]);

  const getTabIndex = useCallback(
    (tabId: string) => {
      return tabs.findIndex((tab) => tab.id === tabId);
    },
    [tabs]
  );

  const focusTab = useCallback((tabId: string) => {
    const tabElement = tabRefs.current[tabId];
    if (tabElement) {
      tabElement.focus();
    }
  }, []);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLButtonElement>, currentTabId: string) => {
      const currentIndex = getTabIndex(currentTabId);
      let targetIndex = currentIndex;

      switch (event.key) {
        case 'ArrowLeft':
          event.preventDefault();
          targetIndex = currentIndex > 0 ? currentIndex - 1 : tabs.length - 1;
          break;
        case 'ArrowRight':
          event.preventDefault();
          targetIndex = currentIndex < tabs.length - 1 ? currentIndex + 1 : 0;
          break;
        case 'Home':
          event.preventDefault();
          targetIndex = 0;
          break;
        case 'End':
          event.preventDefault();
          targetIndex = tabs.length - 1;
          break;
        case 'ArrowDown':
          event.preventDefault();
          // Move focus to tabpanel when ArrowDown is pressed
          if (onFocusPanel) {
            onFocusPanel(tabs[currentIndex].id);
          }
          return;
        case 'Enter':
        case ' ':
          event.preventDefault();
          onTabChange(tabs[currentIndex].index);
          // Move focus to tabpanel after activation
          if (onFocusPanel) {
            // Small delay to allow tabpanel to be rendered/visible
            setTimeout(() => {
              onFocusPanel(tabs[currentIndex].id);
            }, 100);
          }
          return;
        default:
          return;
      }

      if (targetIndex !== currentIndex && targetIndex >= 0 && targetIndex < tabs.length) {
        const targetTab = tabs[targetIndex];
        onTabChange(targetTab.index);
        // Focus the tab after state update
        setTimeout(() => {
          focusTab(targetTab.id);
        }, 0);
      }
    },
    [tabs, getTabIndex, onTabChange, focusTab, onFocusPanel]
  );

  const setTabRef = useCallback((tabId: string, element: HTMLButtonElement | null) => {
    tabRefs.current[tabId] = element;
  }, []);

  return {
    handleKeyDown,
    setTabRef,
    activeTabIndex: getActiveTabIndex(),
  };
};
